import React from "react";
import { connect } from "react-redux";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import imgLogo from "../images/specmatelogo.webp";
import ModalContainer from "./Modals/ModalContainer";
import TopMenu from "./Menus/TopMenu";
import UnitDesign from "./UnitDesign";
import Drawings from "./Drawings";
import UnitLayout from "./UnitLayout";
import ProjectManagement from "./Project/ProjectManagement";
import SpecItems from "./SpecItems";
import Outputs from "./Outputs";
import Pricing from "./Pricing";
import LoginStart from "./Auth/LoginStart";
import IdentityLogin from "./Auth/IdentityLogin";
import IdentityLogout from "./Auth/IdentityLogout";
import TopRightMenu from "./Menus/TopRightMenu";
import TopRightPricing from "./Menus/TopRightPricing";
import ProjectHeader from "./Menus/ProjectHeader";
import ProjectManagementHeader from "./Menus/ProjectManagementHeader";
import ServerError from "./Modals/ServerError";
import ModalNotifications from "./Modals/ModalNotifications";
import Loading from "./Shared/Loading";
import ErrorBoundary from "./Shared/ErrorBoundary";
import { mapDispatchToProps } from "../redux/actions";
import { syncAuthToRedux } from "../security/authStorage";
import PrivateRoute from "./PrivateRoute";
import { getAppVersionNumber } from "../api-calls/systemDataService";
import { getPdfProcessingRecords } from "../api-calls/pdfGeneratorService";
import { isEmptyObject, isNullOrEmpty} from "../utilities/helpers";
import "../css/App.scss";
import "../css/TopMenu2.scss";
import FactoryRequestMain from "./FactoryRequests/FactoryRequestMain";
import { addNotification } from "../api-utilities/notification-tools";

// expected props
interface IProps {
  updateRedux: any;
  reduxAppVersion: any;
  reduxOutputDocumentsInfo: any;
}
// local state
interface IState {}

class App extends React.Component<IProps, IState> {
  timer: any; //define a timer variable for our SetTimeout

  async componentDidMount() {
    //Check if there's a valid auth entry in localstorage.  If so, load it back into reduxAuth.
    //This needs to be done to reload the userId, access_token, etc. back into redux if the site reloads.
    syncAuthToRedux();

    //Get appplication data
    let response = await getAppVersionNumber();
    if (response && !response.hasError) {
      if(!isEmptyObject(response.applicationDataRecords)){
           let version = response.applicationDataRecords.find((item: any) => item.key == "ProgramVersion");
            if (version != null) {
              this.props.updateRedux("UPDATE_APP_VERSION", { appVersion: version.value });
            }
            
            let bannerText = response.applicationDataRecords.find((item: any) => item.key == "AlertBanner");
            if (bannerText != null && !isNullOrEmpty(bannerText.value)) {
              const notification = {
                  id: "userCasingHeightWidthError",
                  notificationType: "banner",
                  title: "Warning",
                  content: <div className="nf-div">{bannerText.value}</div>
                };
                addNotification(notification);
            }
        }
    }
  }

  async componentDidUpdate(prevProps: any) {
    if (prevProps.reduxOutputDocumentsInfo.projectId !== this.props.reduxOutputDocumentsInfo.projectId) {
      clearTimeout(this.timer);
    }

    if (
      this.props.reduxOutputDocumentsInfo.projectId !== null &&
      prevProps.reduxOutputDocumentsInfo.pollForUpdates === false &&
      this.props.reduxOutputDocumentsInfo.pollForUpdates === true
    ) {
      await this.getPDFRecordsAndConfigurePolling();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  private async getPDFRecordsAndConfigurePolling() {
    //console.log('GettingData From App page projectDataAPI');
    const previousActiveRecordsCount = this.props.reduxOutputDocumentsInfo.activeProcessingRecordsCount;
    const previousShowDocsReadyNotificationIcon = this.props.reduxOutputDocumentsInfo.showDocsReadyNotificationIcon;
    const isOnOutputPage = this.props.reduxOutputDocumentsInfo.isOutputPage;
    const response = await getPdfProcessingRecords(this.props.reduxOutputDocumentsInfo.projectId);
    if (!response?.data.hasError) {
      //Check if any records are actively running (status not equal to completed or failed)
      let dtos = response?.data.pdfProcessingDTOs;
      let activeRecords = dtos.filter(
        (item: any) => item.processingStatusName.toLowerCase() !== "completed" && item.processingStatusName.toLowerCase() !== "failed"
      );
      this.props.updateRedux("UPDATE_ACTIVEOUTPUTDOCUMENTS", {
        pdfRecords: dtos,
        activeProcessingRecordsCount: activeRecords.length,
        pollForUpdates: activeRecords.length > 0 ? true : false,
        showDocsReadyNotificationIcon:
          (previousShowDocsReadyNotificationIcon || activeRecords.length < previousActiveRecordsCount) && !isOnOutputPage ? true : false
      });
      //console.log(this.props.reduxOutputDocumentsInfo.showDocsReadyNotificationIcon);
      await this.PollForStatusUpdates();
    }
  }

  private async PollForStatusUpdates() {
    if (this.props.reduxOutputDocumentsInfo.pollForUpdates == true) {
      clearTimeout(this.timer);
      //poll for updates recursively
      this.timer = setTimeout(() => this.getPDFRecordsAndConfigurePolling(), 5000);
    } else {
      clearTimeout(this.timer);
    }
  }

  public render() {
    return (
      <BrowserRouter>
        {/* Outer boundary: catches a crash anywhere in the app shell (header, menus, etc.), not just
            in the routed page content below - e.g. a header component reading corrupted auth data. */}
        <ErrorBoundary>
        <div className="app">
            <ModalContainer />

            <header className="app-fixed-header">
              <div className="app-header-table">
                <div className="app-header-td1">
                  <img src={imgLogo} className="app-logo-img" alt="logo" />
                </div>
                <div className="app-header-td2">
                  <ProjectHeader />
                </div>
                <div className="app-header-td3">
                  <TopRightMenu />
                </div>
                <div className="app-header-td4">
                  {/** Pricing only appears for pages that contain /pid in the route **/}
                  <Route path="/pid/:projectId/uid/:unitId" component={TopRightPricing} />
                </div>
              </div>
            </header>

            <div className="app-chevron-bar">
              {/** Chevron TopMenu bar only appears for pages that contain /pid in the route **/}
              <Route path="/pid/:projectId/uid/:unitId" component={TopMenu} />

              {/** Special header for Project Management page with all inactive chevron links **/}
              <Route path="/project" component={ProjectManagementHeader} />
            </div>

            <div className="app-body">
              <div className="app-body-top"></div>

              <Route path="/" exact component={LoginStart} />
              <Route path="/loginstart" exact component={LoginStart} />

              <Route path="/login" exact component={IdentityLogin} />
              <Route path="/logout" exact component={IdentityLogout} />

              {/* Routes the user cannot get to unless they're logged in. */}
              {/* NOTE: PrivateRoute.tsx is a custom component we built to handle these routes */}
              {/* ErrorBoundary catches any crash in a routed page (e.g. a page assuming an API call
                  succeeded when the token was actually invalid) so it doesn't blank out the whole app. */}
              <ErrorBoundary>
                <Switch>
                  <PrivateRoute path="/project" component={ProjectManagement} />
                  <PrivateRoute path="/pid/:projectId/uid/:unitId/drawing" component={Drawings} />
                  <PrivateRoute path="/pid/:projectId/uid/:unitId/design" component={UnitDesign} />
                  <PrivateRoute path="/pid/:projectId/uid/:unitId/layout" component={UnitLayout} />
                  <PrivateRoute path="/pid/:projectId/uid/:unitId/spec" component={SpecItems} />
                  <PrivateRoute path="/pid/:projectId/uid/:unitId/outputs" component={Outputs} />
                  <PrivateRoute path="/pid/:projectId/uid/:unitId/factoryrequests" component={FactoryRequestMain} />
                  {/* <PrivateRoute path="/pid/:projectId/uid/:unitId/pricing" component={Pricing} /> */}
                </Switch>
              </ErrorBoundary>

              <div className="app-body-bottom"></div>
            </div>

            <section className="app-footer">
              <div className="app-footer-content">Version {this.props.reduxAppVersion.appVersion}</div>
            </section>
          <ModalNotifications />
          <ServerError />
          <Loading />
        </div>
        </ErrorBoundary>
      </BrowserRouter>
    );
  }
}

//------------------ Redux ----------------------------
function mapStateToProps(state: any) {
  return {
    reduxAppVersion: state.reduxAppVersion,
    reduxOutputDocumentsInfo: state.reduxOutputDocumentsInfo
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(App);
